import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { AnalyticalVariable } from '@verantos-dev/types-cohorts';
import type { ListResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';

/**
 * Discriminator on the AV wire shape. Matches the chat-side artifact kind
 * (`EpiArtifactKind` in `feature-epi-chat`) so render paths can be shared.
 *
 * - `av_ir`: structured AV — `jsonValue` populated, `sqlBody` null.
 * - `av_denovo`: SQL-first AV (de novo / agent path) — `sqlBody` is the
 *   authoritative definition, `jsonValue` null. Backend derives this from
 *   `JSON_VALUE` presence; no separate column.
 */
export type AvKind = 'av_ir' | 'av_denovo';

export interface AnalyticalVariableListItem {
  analyticalVariableId: string;
  name: string;
  dataElement: string | null;
  description: string;
  format: string;
  // AVs are dual-shape (structured jsonValue OR sql_body), discriminated by
  // `kind`. The wire always carries both fields with one null; consumers
  // branch on `kind` rather than probing for presence.
  jsonValue: AnalyticalVariable | null;
  sqlBody: string | null;
  kind: AvKind;
  owner: string;
  // Owning organization. The backend always sends ``organizationId`` and resolves
  // ``organizationName`` only for internal (Verantos) users (who get the org
  // column); it is null for external users' single-org listings. Both are
  // optional on the wire type so synthetic/placeholder list entries (e.g. the
  // optimistic cache row in ActiveChatContext) needn't fabricate org data.
  organizationId?: string;
  organizationName?: string | null;
  createdBy: { userId: string; transactionDate: string };
  // Cheap persistence-tier signals carried on the list shape too —
  // see ``AnalyticalVariableDetail`` for the manifest-vs-column status
  // distinction. ``manifest`` is deliberately absent from the list
  // payload (multi-KB per row); consumers fetch detail for the full
  // manifest.
  status?: string | null;
  scope?: string | null;
  // True/False when index-date dependency is determined server-side; null when
  // a transitive AV reference could not be resolved (indeterminate).
  indexDependent?: boolean | null;
  // 'patient' | 'event' | null/undefined — null (no declaration, or the flag
  // off) is treated as 'patient' by every consumer. A filter INPUT for the
  // pickers to pre-disable/badge a row; the server-side gate at bind time is
  // the authority, never this field alone.
  rowLevel?: 'patient' | 'event' | null;
  // SQL output-shape flags. A filter INPUT for pickers to pre-disable a row under a
  // conditional ("all other criteria met") index; the server-side gate at bind time
  // is the authority. Undefined/null when the server did not compute them.
  hasCandidateRows?: boolean | null;
  hasIndexDateColumn?: boolean | null;
  hasEventDateColumn?: boolean | null;
}

/**
 * TanStack Query hook for listing analytical variables in the AV library.
 * Calls GET /analytical-variables with optional search params (page, pageSize, sort, quickFilter).
 * Pass projectId when listing from a cohort context to scope results to that project's organization.
 *
 */
export const useAnalyticalVariableListQuery = (searchParams?: string, projectId?: string) => {
  const urlParams = new URLSearchParams(searchParams ?? '');
  if (projectId) urlParams.set('projectId', projectId);
  const queryString = urlParams.toString() ? `?${urlParams.toString()}` : '';
  return useQuery({
    queryKey: [...avKeys.library(), searchParams, projectId],
    queryFn: () =>
      authFetch<ListResponse<AnalyticalVariableListItem>>(`/analytical-variables${queryString}`),
    // Keep the previous page's response on screen while the next page fetches.
    // Without it, ``data`` goes undefined on a page switch (the queryKey changes),
    // the grid's server ``rowCount`` collapses to 0, and MUI DataGrid clamps the
    // page back to 1 — the flicker/reset on every page change.
    placeholderData: keepPreviousData,
    // OR-6209: always refetch the library list when a consumer (re)mounts.
    // The "+ Add variable" dataset picker and the "+ Add criteria" cohort
    // picker both read their rows from this query but stay UNMOUNTED until the
    // user opens the modal. When an AV's description is edited (agent
    // RESOURCE_UPDATED or a manual save) the ``avKeys.library()`` invalidation
    // fires while these picker queries are inactive observers — and the app's
    // global ``staleTime: 5min`` (see app QueryClient) means the cached list is
    // still considered fresh, so the default ``refetchOnMount: true`` is a
    // no-op on reopen and the picker keeps showing the OLD description while the
    // AV detail view shows the new one. ``'always'`` forces a fresh fetch every
    // time a picker mounts, so the list reflects the current definition
    // regardless of staleTime or which edit path triggered the change.
    refetchOnMount: 'always',
  });
};
